import React, { useState, useEffect, useRef, useCallback } from 'react';
import { List, Avatar, Input, Button, Badge, Modal, message, Empty, Spin, Tag } from 'antd';
import {
  UserOutlined, MessageOutlined, ArrowLeftOutlined, SendOutlined,
  EditOutlined, TeamOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { useNavigate, useLocation } from 'react-router-dom';

const fmtTime = (t) => {
  if (!t) return '';
  const d = dayjs(t);
  if (d.isSame(dayjs(), 'day')) return d.format('HH:mm');
  if (d.isSame(dayjs().subtract(1, 'day'), 'day')) return '昨天';
  if (d.isSame(dayjs(), 'year')) return d.format('MM-DD');
  return d.format('YYYY-MM-DD');
};

const Messages = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialPeerHandled = useRef(false);
  const [view, setView] = useState('list');           // list | chat
  const [conversations, setConversations] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const [peer, setPeer] = useState(null);             // {id, nickname, avatar}
  const [msgs, setMsgs] = useState([]);
  const [loadingChat, setLoadingChat] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);

  // 发起新会话
  const [pickOpen, setPickOpen] = useState(false);
  const [tsContacts, setTsContacts] = useState([]);

  const fetchConversations = useCallback(() => {
    setLoadingList(true);
    axios.get('/messages/conversations')
      .then((r) => { if (r.data?.success) setConversations(r.data.conversations || []); })
      .catch(() => {})
      .finally(() => setLoadingList(false));
  }, []);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  // 打开与某人的对话
  const openChat = useCallback((u) => {
    setPeer({ id: u.id || u.user_id, nickname: u.nickname, avatar: u.avatar });
    setMsgs([]);
    setView('chat');
    setPickOpen(false);
  }, []);

  // 从其它页面（如 TS 平台点上级/下级）带 peer 进来时，自动打开对话
  useEffect(() => {
    const p = location.state?.peer;
    if (!initialPeerHandled.current && p && (p.id || p.user_id)) {
      initialPeerHandled.current = true;
      openChat(p);
    }
  }, [location.state, openChat]);

  const loadThread = useCallback((peerId, showLoading) => {
    if (!peerId) return;
    if (showLoading) setLoadingChat(true);
    axios.get(`/messages/thread/${peerId}`)
      .then((r) => {
        if (r.data?.success) {
          setMsgs(r.data.messages || []);
          if (r.data.peer) setPeer((p) => ({ ...p, ...r.data.peer }));
        }
      })
      .catch(() => {})
      .finally(() => { if (showLoading) setLoadingChat(false); });
  }, []);

  // 进入对话：拉取 + 轮询
  useEffect(() => {
    if (view === 'chat' && peer?.id) {
      loadThread(peer.id, true);
      pollRef.current = setInterval(() => loadThread(peer.id, false), 8000);
      return () => clearInterval(pollRef.current);
    }
  }, [view, peer?.id, loadThread]);

  // 新消息滚到底
  useEffect(() => {
    if (view === 'chat') bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [msgs, view]);

  const backToList = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setView('list');
    setPeer(null);
    setText('');
    fetchConversations();
  };

  const send = async () => {
    const content = text.trim();
    if (!content || !peer?.id) return;
    setSending(true);
    try {
      const r = await axios.post('/messages', { recipient_id: peer.id, content });
      if (r.data?.success) {
        setText('');
        // 乐观追加
        setMsgs((prev) => [...prev, { id: r.data.id, content, from_me: true, created_at: r.data.created_at }]);
        loadThread(peer.id, false);
      } else {
        message.error(r.data?.message || '发送失败');
      }
    } catch (e) {
      message.error('发送失败');
    } finally {
      setSending(false);
    }
  };

  // 搜索用户
  const openPicker = () => {
    setPickOpen(true);
    axios.get('/messages/ts-contacts')
      .then((r) => { if (r.data?.success) setTsContacts(r.data.contacts || []); })
      .catch(() => {});
  };

  // ====== 会话列表视图 ======
  if (view === 'list') {
    return (
      <div style={{ maxWidth: 560, margin: '0 auto', padding: '12px 12px 28px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ArrowLeftOutlined onClick={() => navigate('/profile')} style={{ fontSize: 18, cursor: 'pointer', color: '#6366F1' }} />
            <span style={{ fontSize: 18, fontWeight: 600 }}>我的私信</span>
          </div>
          <Button type="primary" icon={<EditOutlined />} size="small" onClick={openPicker} style={{ borderRadius: 10 }}>发起私信</Button>
        </div>

        {loadingList ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : conversations.length === 0 ? (
          <Empty description="还没有私信，点右上角「发起私信」开始聊天" style={{ marginTop: 60 }} />
        ) : (
          <div style={{ background: '#fff', borderRadius: 16 }}>
            <List
              dataSource={conversations}
              renderItem={(c) => (
                <List.Item onClick={() => openChat(c)} style={{ cursor: 'pointer', padding: '12px 14px' }}>
                  <List.Item.Meta
                    avatar={<Badge count={c.unread} size="small"><Avatar src={c.avatar} icon={<UserOutlined />} /></Badge>}
                    title={<div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 15, color: '#1f2430' }}>{c.nickname || '用户'}</span>
                      <span style={{ fontSize: 12, color: '#bbb' }}>{fmtTime(c.last_time)}</span>
                    </div>}
                    description={<span style={{ color: '#8c8c8c', fontSize: 13 }}>
                      {c.last_from_me ? '我: ' : ''}{(c.last_content || '').slice(0, 30)}
                    </span>}
                  />
                </List.Item>
              )}
            />
          </div>
        )}

        {/* 发起私信：仅限 TS 上下级 */}
        <Modal open={pickOpen} onCancel={() => setPickOpen(false)} footer={null} title="选择上下级发起私信" styles={{ body: { paddingTop: 8 } }}>
          <div style={{ fontSize: 12, color: '#999', margin: '0 0 8px' }}><TeamOutlined /> 私信仅限与你的上级 / 下级代理沟通</div>
          {tsContacts.length > 0 ? (
            <List
              dataSource={tsContacts}
              renderItem={(u) => (
                <List.Item onClick={() => openChat(u)} style={{ cursor: 'pointer' }}>
                  <List.Item.Meta
                    avatar={<Avatar src={u.avatar} icon={<UserOutlined />} />}
                    title={<span>{u.nickname} <Tag color={u.relation === '上级' ? 'blue' : 'green'} style={{ marginLeft: 4 }}>{u.relation}</Tag></span>}
                  />
                </List.Item>
              )}
            />
          ) : (
            <Empty description="暂无上级或下级代理" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </Modal>
      </div>
    );
  }

  // ====== 聊天视图 ======
  return (
    <div style={{ maxWidth: 560, margin: '0 auto', height: 'calc(100vh - 20px)', display: 'flex', flexDirection: 'column' }}>
      {/* 顶部栏 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderBottom: '1px solid #f0f0f0', background: '#fff' }}>
        <ArrowLeftOutlined onClick={backToList} style={{ fontSize: 18, cursor: 'pointer', color: '#6366F1' }} />
        <Avatar size={32} src={peer?.avatar} icon={<UserOutlined />} />
        <span style={{ fontSize: 16, fontWeight: 600 }}>{peer?.nickname || '对话'}</span>
      </div>

      {/* 消息区 */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 12px', background: '#f5f6fa' }}>
        {loadingChat ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : msgs.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#bbb', marginTop: 40 }}>
            <MessageOutlined style={{ fontSize: 28 }} /><div style={{ marginTop: 8 }}>发条消息开始聊天吧</div>
          </div>
        ) : (
          msgs.map((m) => (
            <div key={m.id} style={{ display: 'flex', justifyContent: m.from_me ? 'flex-end' : 'flex-start', marginBottom: 12 }}>
              <div style={{ maxWidth: '72%' }}>
                <div style={{
                  padding: '9px 13px', borderRadius: 14,
                  background: m.from_me ? '#6366F1' : '#fff',
                  color: m.from_me ? '#fff' : '#1f2430',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.06)', wordBreak: 'break-word', whiteSpace: 'pre-wrap',
                }}>{m.content}</div>
                <div style={{ fontSize: 11, color: '#c5c8ce', textAlign: m.from_me ? 'right' : 'left', marginTop: 3 }}>
                  {dayjs(m.created_at).format('MM-DD HH:mm')}
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {/* 输入区 */}
      <div style={{ display: 'flex', gap: 8, padding: '10px 12px', borderTop: '1px solid #f0f0f0', background: '#fff' }}>
        <Input.TextArea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="输入消息…"
          autoSize={{ minRows: 1, maxRows: 4 }}
          onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); send(); } }}
          style={{ borderRadius: 10 }}
        />
        <Button type="primary" icon={<SendOutlined />} loading={sending} onClick={send} style={{ borderRadius: 10, height: 'auto' }}>发送</Button>
      </div>
    </div>
  );
};

export default Messages;
